import classNames from 'classnames';
import { partition, sortBy, uniqBy } from 'lodash-es';
import moment from 'moment';
import type { Op } from 'quill/core';
import React, { useEffect, useState } from 'react';
import ActivityItem, { ActivitySeparator } from './ActivityItem';
import * as styles from './ControlBarActivity.module.scss';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import InfiniteScrollWaypoint from '../../components/InfiniteScrollWaypoint';
import LoadingIndicator from '../../components/LoadingIndicator';
import SVGIcon from '../../components/SVGIcon';
import { EditorActions } from '../../editor/editorSlice';
import { gql } from '../../graphql/generated/gql';
import type { DocumentType } from '../../graphql/generated/gql';
import { PostActivityAction } from '../../graphql/generated/graphql';
import archiveIcon from '../../icons/non-prefilled/16x16/archive.svg';
import draftsIcon from '../../icons/non-prefilled/16x16/drafts.svg';
import editIcon from '../../icons/non-prefilled/16x16/edit.svg';
import publishIcon from '../../icons/non-prefilled/16x16/publish.svg';
import reorderIcon from '../../icons/non-prefilled/16x16/reorder.svg';
import unarchiveIcon from '../../icons/non-prefilled/16x16/unarchive.svg';
import unpublishIcon from '../../icons/non-prefilled/16x16/unpublish.svg';
import verifiedIcon from '../../icons/non-prefilled/16x16/verifiedFilled.svg';
import { useGetUser } from '../../user/useAllUsers';
import exhaustiveMatchGuard from '../../utils/exhaustiveMatchGuard';
import { isFeatureEnabled } from '../../utils/features';
import Logger from '../../utils/Logger';
import { isCommentOrPublicCommentThread } from '../../utils/post/threadUtils';
import getFirstName from '../../utils/text/getFirstName';
import commentThreadHash from '../../utils/url/commentThreadHash';
import historyPath from '../../utils/url/historyPath';
import partitionHistories from '../history/partitionHistories';
import type { PartitionedHistory } from '../history/partitionHistories';
import Stats from '../Stats';
import useCanDisplayNewPostHistoryView from '../useCanDisplayNewPostHistoryView';
import CommentActivityItem, { isCommentActivity } from './CommentActivityItem';
import Comment from '../../editor/formats/Comment';

export const CONTROL_BAR_ACTIVITY_MIN_HEIGHT = 265;

// Do initial fetch with a fixed time to enable caching
// TODO: This will not be needed when beforeAt becomes optional
export const POST_ACTIVITIES_END_OF_TIME = '2099-12-31T00:00:00.0Z';

gql(/** @complexity 2 */ `
  fragment ControlBarActivity_commentThread on CommentThread {
    id
    content
    context
    comments {
      id
      content
    }
  }
`);

const POST_ACTIVITY_FRAGMENT = gql(`
  fragment ControlBarActivity_activities on PostActivity {
    actedAt
    action
    actors {
      id
      ...useGetUser_user
    }
    source {
      id
      ... on Comment {
        content
        thread {
          id
          ...ControlBarActivity_commentThread
        }
      }
      ... on CommentThread {
        id
        ...ControlBarActivity_commentThread
      }
    }
  }
`);

const POST_FRAGMENT = gql(`
  fragment ControlBarActivity_post on Post {
    id
    threads {
      id
      ... on CommentThread {
        id
        context
        comments {
          id
          insertedAt
          content
          author {
            id
            ...useGetUser_user
          }
        }
      }
    }
    histories {
        id
        stats {
          ...Stats_stats
        }
        versionFrom
        versionTo
        authors {
          id
          ...useGetUser_user
        }
        ...partitionHistories_history
      }
    ...historyPath_post
  }
`);

interface ControlBarActivityProps {
  post?: DocumentType<typeof POST_FRAGMENT>;
  activities?: DocumentType<typeof POST_ACTIVITY_FRAGMENT>[];
  unresolvedCommentThreadIds: string[];
  fetchMoreActivities: () => Promise<void>;
  closePopover: () => void;
  fullHeight?: boolean;
  lastViewedAt: number;
}

const ControlBarActivity = ({
  post,
  activities,
  closePopover,
  unresolvedCommentThreadIds,
  fetchMoreActivities,
  lastViewedAt,
  fullHeight,
}: ControlBarActivityProps) => {
  const dispatch = useAppDispatch();
  const [canShowLoader, setCanShowLoader] = useState(false);
  useEffect(() => {
    const timeout = setTimeout(() => setCanShowLoader(true), 500);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const focusCommentThread = (threadId: string) => {
    dispatch(
      EditorActions.focusMagneticWall({
        id: threadId,
        type: 'comment',
        isActive: true,
        hasExpandIntent: false,
      }),
    );
    closePopover();
  };

  if (!activities || !post) {
    return (
      canShowLoader && (
        <div className={styles.loader}>
          <LoadingIndicator delay={0} />
        </div>
      )
    );
  }

  return (
    <div
      className={classNames(styles.container, {
        [styles.fullHeight]: fullHeight,
      })}
    >
      <UnresolvedThreadsSection
        post={post}
        unresolvedCommentThreadIds={unresolvedCommentThreadIds}
        focusThread={focusCommentThread}
      />
      <AllActivitiesSection
        post={post}
        activities={activities}
        unresolvedCommentThreadIds={unresolvedCommentThreadIds}
        focusCommentThread={focusCommentThread}
        fetchMoreActivities={fetchMoreActivities}
        lastViewedAt={lastViewedAt}
      />
    </div>
  );
};

interface AllActivitiesSectionProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  activities: DocumentType<typeof POST_ACTIVITY_FRAGMENT>[];
  unresolvedCommentThreadIds: string[];
  focusCommentThread: (id: string) => void;
  lastViewedAt: number;
  fetchMoreActivities: () => Promise<void>;
}

const AllActivitiesSection = ({
  post,
  activities,
  unresolvedCommentThreadIds,
  focusCommentThread,
  lastViewedAt,
  fetchMoreActivities,
}: AllActivitiesSectionProps) => {
  const historyActivities = useEditActivities(activities, post);
  const commentActivities = useCommentActivities(
    activities,
    post,
    unresolvedCommentThreadIds,
    focusCommentThread,
  );

  const recentActivitiesThreshold = lastViewedAt ? moment(lastViewedAt) : null;
  const lastFetchedActivity = activities.at(-1);

  const standardActivities: ActivityView[] = activities
    .map((item) => {
      if (
        item.action === PostActivityAction.Edit ||
        isCommentActivity(item.action)
      ) {
        return null;
      }

      return {
        key: `${item.actedAt}-${item.action}`,
        timestamp: new Date(item.actedAt).getTime(),
        view: <StandardPostActivity action={item.action} activity={item} />,
      };
    })
    .filter((item) => item != null);

  const allActivities = uniqBy(
    [...standardActivities, ...historyActivities, ...commentActivities],
    (item) => item.key,
  );
  const allActivitiesSorted = sortBy(allActivities, (item) => -item.timestamp);

  const [recentActivities, oldActivities] = partition(
    allActivitiesSorted,
    (item) => recentActivitiesThreshold?.isBefore(item.timestamp),
  );

  const hasMoreActivitiesToFetch =
    lastFetchedActivity &&
    lastFetchedActivity.action !== PostActivityAction.Create;
  return (
    <>
      <h3 className={styles.title}>Activities</h3>
      <div>
        {recentActivities?.map((activity) => (
          <React.Fragment key={activity.key}>{activity.view}</React.Fragment>
        ))}
        {oldActivities.length > 0 && (
          <>
            {recentActivitiesThreshold && (
              <ActivitySeparator>
                Your last view was {recentActivitiesThreshold.fromNow()}
              </ActivitySeparator>
            )}
            {oldActivities?.map((activity) => (
              <React.Fragment key={activity.key}>
                {activity.view}
              </React.Fragment>
            ))}
          </>
        )}
      </div>
      {hasMoreActivitiesToFetch && (
        <InfiniteScrollWaypoint fetchMore={fetchMoreActivities} />
      )}
    </>
  );
};

const useEditActivities = (
  activities: DocumentType<typeof POST_ACTIVITY_FRAGMENT>[] | undefined,
  post: DocumentType<typeof POST_FRAGMENT> | undefined,
): ActivityView[] => {
  const getUser = useGetUser();
  const canDisplayNewPostHistoryView = useCanDisplayNewPostHistoryView();

  if (!activities || !post) {
    return [];
  }

  const { accessibleHistories, restrictedHistories } = partitionHistories(
    post.histories,
  );
  const lastFetchedActivity = activities.at(-1);

  return [...accessibleHistories, ...restrictedHistories]
    .filter(
      (item) =>
        item.authors.length > 0 &&
        new Date(lastFetchedActivity?.actedAt) <= new Date(item.insertedAt),
    )
    .map((history): ActivityView => {
      const actors = history.authors.map(getUser);
      return {
        key: `${history.insertedAt}-history}`,
        timestamp: new Date(history.insertedAt).getTime(),
        view: (
          <ActivityItem
            actors={actors}
            withActorsNames
            icon={<SVGIcon className={styles.editIcon}>{editIcon}</SVGIcon>}
            timestamp={history.insertedAt}
            description={
              history.hasStats ? (
                <Stats stats={history.stats} editsLabel="Words edited" />
              ) : (
                'Edited this post'
              )
            }
            href={getLinkToHistory(post, canDisplayNewPostHistoryView, history)}
          />
        ),
      };
    });
};

const useCommentActivities = (
  activities: DocumentType<typeof POST_ACTIVITY_FRAGMENT>[] | undefined,
  post: DocumentType<typeof POST_FRAGMENT> | undefined,
  unresolvedCommentThreadIds: string[],
  focusCommentThread: (id: string) => void,
): ActivityView[] => {
  const getUser = useGetUser();
  const dispatch = useAppDispatch();
  const canDisplayNewPostHistoryView = useCanDisplayNewPostHistoryView();

  if (!activities || !post) {
    return [];
  }

  const { accessibleHistories, restrictedHistories } = partitionHistories(
    post.histories,
  );

  const allHistories = [...accessibleHistories, ...restrictedHistories];

  return activities
    .map((activity) => {
      const actors = activity.actors.map(getUser);

      const content =
        activity.source?.__typename === 'CommentThread' ||
        activity.source?.__typename === 'Comment'
          ? activity.source.content
          : null;

      const thread =
        activity.source?.__typename === 'CommentThread'
          ? activity.source
          : activity.source?.__typename === 'Comment'
            ? activity.source.thread
            : null;

      if (!content || !thread || !isCommentActivity(activity.action)) {
        return null;
      }

      const getContext = () => {
        if (
          activity.action === 'COMMENT_REPLY' &&
          activity.source?.__typename === 'Comment'
        ) {
          const id = activity.source?.id;
          const index = thread.comments.findIndex(
            (comment) => comment.id === id,
          );
          return thread.comments[index - 1]?.content;
        }

        return extractCommentThreadContext(thread.context, thread.id);
      };

      return {
        key: `${activity.actedAt}-${activity.action}`,
        timestamp: new Date(activity.actedAt).getTime(),
        view: (
          <CommentActivityItem
            timestamp={activity.actedAt}
            actors={actors}
            type={activity.action}
            content={content}
            context={getContext()}
            {...(unresolvedCommentThreadIds.includes(thread.id)
              ? {
                  onClick: () => focusCommentThread(thread.id),
                  onMouseEnter: () =>
                    dispatch(EditorActions.highlightComment(thread.id)),
                  onMouseLeave: () =>
                    dispatch(EditorActions.removeMagneticWallHighlight()),
                }
              : {
                  href: getLinkToHistory(
                    post,
                    canDisplayNewPostHistoryView,
                    allHistories.findLast((history) =>
                      moment(history.insertedAt).isSameOrAfter(
                        activity.actedAt,
                      ),
                    ),
                    thread.id,
                  ),
                })}
          />
        ),
      };
    })
    .filter((item) => item != null);
};

const getLinkToHistory = (
  post: DocumentType<typeof POST_FRAGMENT> | undefined,
  canDisplayNewPostHistoryView: boolean | null,
  history?: PartitionedHistory<
    DocumentType<typeof POST_FRAGMENT>['histories'][number]
  >,
  threadId?: string,
) => {
  if (!post || !history) {
    return undefined;
  }

  const path =
    historyPath(post, history.versionFrom, history.versionTo) +
    (threadId ? commentThreadHash(threadId) : '');

  if (!isFeatureEnabled('history_v2')) {
    return threadId ? undefined : path;
  }

  return history.isAccessible && canDisplayNewPostHistoryView
    ? path
    : undefined;
};

interface StandardPostActivityProps {
  action: Exclude<
    PostActivityAction,
    | PostActivityAction.Edit
    | PostActivityAction.CommentCreate
    | PostActivityAction.CommentReply
    | PostActivityAction.CommentResolve
  >;
  activity: Omit<DocumentType<typeof POST_ACTIVITY_FRAGMENT>, 'action'>;
}

const StandardPostActivity = ({
  action,
  activity,
}: StandardPostActivityProps) => {
  const getUser = useGetUser();
  const actors = activity.actors.map(getUser);

  const descriptionWithActors = (description: React.ReactNode) => (
    <div className={styles.activityDescription}>
      <strong>
        {activity.actors
          .map((actor) => getFirstName(getUser(actor).name))
          .join(', ')}
      </strong>{' '}
      {description}
    </div>
  );

  switch (action) {
    case PostActivityAction.Create:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.createIcon}>{draftsIcon}</SVGIcon>}
          description={descriptionWithActors('created this post')}
        />
      );
    case PostActivityAction.Verify:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.verifyIcon}>{verifiedIcon}</SVGIcon>}
          description={descriptionWithActors('verified this post')}
        />
      );
    case PostActivityAction.Publish:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.publishIcon}>{publishIcon}</SVGIcon>}
          description={descriptionWithActors('published this post')}
        />
      );
    case PostActivityAction.Unpublish:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={
            <SVGIcon className={styles.unpublishIcon}>{unpublishIcon}</SVGIcon>
          }
          description={descriptionWithActors('unpublished this post')}
        />
      );
    case PostActivityAction.Archive:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.archiveIcon}>{archiveIcon}</SVGIcon>}
          description={descriptionWithActors('archived this post')}
        />
      );
    case PostActivityAction.Unarchive:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={
            <SVGIcon className={styles.unarchiveIcon}>{unarchiveIcon}</SVGIcon>
          }
          description={descriptionWithActors('unarchived this post')}
        />
      );
    case PostActivityAction.TransferOwner:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={
            <SVGIcon className={styles.transferIcon}>{reorderIcon}</SVGIcon>
          }
          description={descriptionWithActors(
            <>
              transferred ownership
              {activity.source?.__typename === 'User' && (
                <>
                  {' '}
                  to{' '}
                  <strong>{getFirstName(getUser(activity.source).name)}</strong>
                </>
              )}
            </>,
          )}
        />
      );

    default: {
      exhaustiveMatchGuard(action);
      const error = `Unknown activity type: ${action as string}`;
      if (process.env.NODE_ENV !== 'production') {
        throw new Error(error);
      }
      Logger.error(error);
      return null;
    }
  }
};

interface ActivityView {
  timestamp: number;
  key: string;
  view: React.ReactNode;
}

interface UnresolvedActivitiesSectionProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  focusThread: (id: string) => void;
  unresolvedCommentThreadIds: string[];
}

const UnresolvedThreadsSection = ({
  post,
  focusThread,
  unresolvedCommentThreadIds,
}: UnresolvedActivitiesSectionProps) => {
  const dispatch = useAppDispatch();
  const getUser = useGetUser();

  const visibleCommentThreads = post?.threads
    ?.map((thread) => {
      const lastComment =
        isCommentOrPublicCommentThread(thread) &&
        unresolvedCommentThreadIds.includes(thread.id) &&
        thread.comments.at(-1);

      if (!lastComment) {
        return null;
      }

      return {
        id: thread.id,
        timestamp: new Date(lastComment.insertedAt).getTime(),
        view: (
          <CommentActivityItem
            actors={[getUser(lastComment.author)]}
            type={
              thread.comments.length > 1
                ? PostActivityAction.CommentReply
                : PostActivityAction.CommentCreate
            }
            timestamp={lastComment.insertedAt}
            context={
              thread.comments.at(-2)?.content ??
              extractCommentThreadContext(thread.context, thread.id)
            }
            content={lastComment.content}
            onClick={() => focusThread(thread.id)}
            onMouseEnter={() =>
              dispatch(EditorActions.highlightComment(thread.id))
            }
            onMouseLeave={() =>
              dispatch(EditorActions.removeMagneticWallHighlight())
            }
          />
        ),
      };
    })
    .filter((item) => item != null)
    .toSorted((a, b) => b.timestamp - a.timestamp);

  if (!visibleCommentThreads?.length) {
    return null;
  }

  return (
    <>
      <h3 className={styles.title}>Unresolved</h3>
      <div>
        {visibleCommentThreads.map((thread) => (
          <React.Fragment key={thread.id}>{thread.view}</React.Fragment>
        ))}
      </div>
    </>
  );
};

const extractCommentThreadContext = (
  ops: Op[] | undefined,
  threadId: string,
) => {
  return ops?.filter?.((item: Op) => {
    const commentAttr = item?.attributes?.[Comment.attrName] as
      | string[]
      | undefined;
    return commentAttr?.includes?.(threadId);
  });
};

export default ControlBarActivity;
